import React, { useEffect, useState, useContext } from 'react';
import dayjs from 'dayjs';
import {
  Alert,
  Badge,
  Button,
  Row,
  Col,
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  Modal,
  ModalHeader,
  ModalBody,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';

import {
  positionCodeToDisplayName,
  specialtyOptions,
} from '../../utils/displayHelper';
import { toStaffRegistration, toJobSearchJobDetail } from '../../routes';
import { Link, navigate } from 'gatsby';
import { excludedSpecialties, formatSalary } from '../../utils/displayHelper';

import ProfilePicture from '../common/ProfilePicture';
import { FaLock, FaFlag, FaExternalLinkAlt, FaEnvelope } from 'react-icons/fa';
import ReportJobForm from '../job-search/ReportJobForm';
import SocialSharing from '../common/SocialSharing';
import PublicEmployerPage from '../job-search/PublicEmployerPage';
import MapWrapper from '../common/MapWrapper';
import {
  JobOwnerDetailViewType,
  JobInternalViewType,
  PartnerJobDetailViewType,
  PartnerJobViewType,
  JobApplicationViewType,
  JobInvitationViewType,
  JobViewType,
} from '../../types/services-api';
import {
  JobPostingCSVJobType,
  JobPostingCSVPartnerType,
} from '../../types/csv';
import { JobType } from '../job-search/JobSearchJobDetail';
import {
  JobSearchContext,
  isCsvJob,
  isDpAuthJob,
  isPartnerAuthJob,
  isPartnerCsvJob,
} from '../../context/JobSearchContext';
import { htmlDecode } from '../../utils/htmlHelper';
import {
  UserContext,
  UserContextType,
  isEmployerType,
  isStaffType,
} from '../../context/UserContext';
import agent from '../../agent';
import OutboundLink from '../common/OutboundLink';
import FormWrapper from '../common/FormWrapper';
import {
  ProfileContext,
  StaffProfileContextType,
} from '../../context/ProfileContext';
import { GlobalContext } from '../../context/GlobalContext';
import JobPostEvaluation from '../common/JobPostEvaluation';

function isEmployerView(obj: any): obj is JobOwnerDetailViewType {
  return 'applys' in obj;
}

export function isDynamicView(obj: any): obj is JobViewType {
  return 'jobId' in obj && 'uri' in obj;
}

const JobPostingView = (props: {
  jobData:
    | JobViewType
    | JobOwnerDetailViewType
    | JobInternalViewType
    | PartnerJobDetailViewType
    | PartnerJobViewType
    | JobPostingCSVJobType
    | JobPostingCSVPartnerType;
  listing?: string;
  employer?: string;
  toggleModal?: () => void;
  modalOpen?: boolean;
  setCurrentJob?: React.Dispatch<React.SetStateAction<JobType | null>>;
}) => {
  const {
    userAuth: { isEmployer },
    updateRedirectPath,
  } = useContext(GlobalContext);
  const { user } = useContext(UserContext) as UserContextType;
  const { profile, updateProfile } = useContext(
    ProfileContext
  ) as StaffProfileContextType;
  const { updatePremiumJob } = useContext(JobSearchContext);
  const { jobData, listing, toggleModal, setCurrentJob } = props;
  const [reportJobModalOpen, setReportJobModalOpen] = useState(false);
  const [tab, setTab] = useState<'EMPLOYER' | 'JOB'>('JOB');
  const [invitation, setInvitation] = useState<
    (JobApplicationViewType & JobInvitationViewType) | null
  >(null);
  const [declineConfirmationOpen, setDeclineConfirmationOpen] = useState(false);

  useEffect(() => {
    if (isDpAuthJob(jobData) && !!jobData.invitedDate) {
      fetchInvitation(jobData.id);
    } else {
      setInvitation(null);
    }
  }, [jobData]);

  function fetchInvitation(jobId: number) {
    agent.Staff.getInvitation(jobId)
      .then((res) => {
        setInvitation(res.data);
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  function toggleDeclineConfirmation() {
    setDeclineConfirmationOpen(!declineConfirmationOpen);
  }

  function declineInvite(e) {
    const { declineMessage, profileVisibility } = e.currentTarget.elements;
    const jobId = isDpAuthJob(jobData) ? jobData.id : 0;
    const message = !!declineMessage.value
      ? declineMessage.value
      : 'Not Provided';
    if (!!profileVisibility && profileVisibility.checked) {
      updateVisibility();
    }
    return agent.Staff.declineInvitation(jobId, message);
  }

  function updateVisibility() {
    agent.Staff.updateVisibility({
      state: false,
    })
      .then((res) => {
        updateProfile({ public: res.data.isPublic });
      })
      .catch((err) => {
        console.log(err.message);
      });
  }

  function handleDeclineSuccess() {
    if (!!invitation) {
      setInvitation({
        ...invitation,
        invitation: {
          ...invitation.invitation,
          declinedDate: dayjs().format('YYYY-MM-DD'),
        },
      });
    }
    toggleDeclineConfirmation();
  }

  const applyButton = isEmployerView(jobData) ? null : (
    <div>
      {'partnerUrl' in jobData ? (
        <Button
          color="secondary"
          className="ms-2"
          tag={OutboundLink}
          href={jobData.partnerUrl}
          disabled={isEmployerType(user)}
        >
          Apply <FaExternalLinkAlt className="fa-xs text-light ms-1" />
        </Button>
      ) : 'applylink' in jobData && !!jobData.applylink ? (
        <Button
          tag={OutboundLink}
          href={jobData.applylink}
          color="primary"
          disabled={isEmployerType(user)}
        >
          Apply <FaExternalLinkAlt className="fa-xs text-light ms-1" />
        </Button>
      ) : (
        'appliedDate' in jobData && (
          <Button
            onClick={toggleModal}
            color="primary"
            data-testid="applyButton"
            disabled={!!jobData.appliedDate || isEmployerType(user)}
          >
            {jobData.appliedDate
              ? `Applied on ${dayjs(jobData.appliedDate).format('MMM D, YYYY')}`
              : 'Apply'}
          </Button>
        )
      )}
    </div>
  );

  if (
    isDpAuthJob(jobData) ||
    isEmployerView(jobData) ||
    (isDynamicView(jobData) && !isCsvJob(jobData))
  ) {
    const employerName = jobData.isConfidential
      ? 'Confidential Employer'
      : isEmployerView(jobData)
        ? jobData.name
        : jobData.employerName;

    const jobPostingDetails = (
      <>
        <Row className="align-items-center mb-4">
          <Col
            lg="7"
            className="d-flex flex-column h-100"
            style={{ wordBreak: 'break-word' }}
          >
            <div className="d-flex flex-wrap">
              <div className="col-12">
                {jobData.status === 'ACTIVE' && (
                  <p className="lead mb-0">{employerName}</p>
                )}
                <p>
                  {jobData.status === 'ACTIVE' && !jobData.isConfidential && (
                    <>
                      {jobData.address} {jobData.address2}
                      <br />
                    </>
                  )}
                  {jobData.city}, {jobData.state} {jobData.zip}
                </p>
              </div>
            </div>
            {jobData.isConfidential && (
              <Alert color="info">
                <p className="mb-0">
                  <Badge color="dark">
                    <FaLock /> Confidential
                  </Badge>
                </p>
                <p className="mb-0">
                  <small>
                    Note: This is a confidential job posting. Some of the
                    employer's information is not displayed.
                  </small>
                </p>
              </Alert>
            )}
          </Col>
          <Col lg="5" style={{ height: '200px' }} className="my-3">
            <MapWrapper jobs={[jobData]} />
          </Col>
        </Row>
        <h6 className="text-secondary">Compensation</h6>
        <p className="mb-0 text-info">
          <small>Base Salary</small>
        </p>
        <p>
          {!!jobData.salary
            ? formatSalary(jobData.salary, jobData.salaryUnits)
            : 'Not Provided'}
        </p>
        <h6 className="text-secondary">
          {jobData.jobDates.length > 0 ? 'Job Dates' : 'Schedule'}
        </h6>
        <div>
          {jobData.jobDates?.length > 0 ? (
            <ul style={{ listStyle: 'none' }} className="ps-0">
              {jobData.jobDates?.map((day) => {
                return (
                  <li key={day.date}>
                    {dayjs(day.date).format('ddd MMMM D, YYYY')}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p>{jobData.dow}</p>
          )}
        </div>
        <h6 className="text-secondary">Description</h6>
        <p
          className="dp-text-pre-wrap"
          dangerouslySetInnerHTML={{ __html: jobData.description }}
        ></p>
        {!isEmployer && (
          <Row>
            <Col className="d-flex justify-content-end align-items-end">
              <Button
                className="ms-auto text-muted"
                color="link"
                title="Report this job posting"
                onClick={() => setReportJobModalOpen(true)}
              >
                <FaFlag />
                <span className="d-none d-md-inline-block ms-2">
                  Report this job posting
                </span>
              </Button>
            </Col>
          </Row>
        )}{' '}
        <ReportJobForm
          jobId={isDynamicView(jobData) ? jobData.jobId : jobData.id}
          flagType={'NONE'}
          isThirdParty={false}
          reportJobModalOpen={reportJobModalOpen}
          setReportJobModalOpen={setReportJobModalOpen}
        />
        {isDynamicView(jobData) && (
          <>
            <p>Create your free account with DentalPost to apply.</p>
            <span
              className="link-primary dp-cursor-pointer"
              onClick={() => {
                updateRedirectPath(`${toJobSearchJobDetail(jobData.jobId)}`);
                navigate(toStaffRegistration);
              }}
            >
              Register & Apply
            </span>
          </>
        )}
      </>
    );

    return (
      <>
        {!!invitation && (
          <>
            <Alert
              color="success"
              isOpen={!!invitation}
              className="d-flex flex-wrap justify-content-between"
            >
              <div>
                <h5>
                  <FaEnvelope /> You've been personally invited to apply to this
                  posting!
                </h5>
                <p>{invitation.invitation.message}</p>
              </div>
              <div className="col-auto ms-auto d-flex">
                {isDpAuthJob(jobData) && !jobData.appliedDate && (
                  <div>
                    {!!invitation.invitation.declinedDate ? (
                      <Button color="info" className="me-3" disabled>
                        Declined on{' '}
                        {dayjs(invitation.invitation.declinedDate).format(
                          'MMM D, YYYY'
                        )}
                      </Button>
                    ) : (
                      <Button
                        color="info"
                        className="me-3"
                        onClick={toggleDeclineConfirmation}
                      >
                        Decline
                      </Button>
                    )}
                  </div>
                )}
                {!invitation.invitation.declinedDate && applyButton}
              </div>
            </Alert>
            <Modal
              isOpen={declineConfirmationOpen}
              toggle={toggleDeclineConfirmation}
            >
              <ModalHeader toggle={toggleDeclineConfirmation}>
                Confirmation Required
              </ModalHeader>
              <ModalBody>
                <FormWrapper
                  formId={'decline-invitation-form'}
                  onSubmit={declineInvite}
                  handleSuccess={handleDeclineSuccess}
                >
                  <FormGroup>
                    <Label>Decline Reason (optional)</Label>
                    <Input
                      type="textarea"
                      rows="3"
                      name="declineMessage"
                    ></Input>
                  </FormGroup>
                  {profile.isPublic && (
                    <FormGroup>
                      <Label>No longer searching for jobs?</Label>
                      <br />
                      <Input type="checkbox" name="profileVisibility" /> Set my
                      profile to private status
                    </FormGroup>
                  )}
                  <div className="text-end my-3">
                    <Button type="submit" color="primary">
                      Submit Response
                    </Button>
                  </div>
                </FormWrapper>
              </ModalBody>
            </Modal>
          </>
        )}
        <div className="d-flex flex-wrap w-100 ">
          <div
            className="col-2 col-md-auto me-3 order-2 order-lg-0"
            style={{ width: '120px', height: '120px' }}
          >
            {'photoUrl' in jobData && jobData.photoUrl ? (
              <img
                src={jobData.photoUrl}
                alt={employerName}
                className="img-fluid me-2"
                style={{ width: '100%', height: '100%' }}
              />
            ) : (
              <ProfilePicture isThirdParty={false} alt={employerName} />
            )}
          </div>
          <div className="col-9 col-lg-5 flex-grow-1 order-3 order-lg-1 me-lg-3">
            {jobData.isNational && (
              <Badge color="secondary" className="me-3">
                National
              </Badge>
            )}
            {jobData.jobDates.length > 0 && (
              <Badge color="primary" className="me-3">
                Temp
              </Badge>
            )}
            <h1 className="h2">
              {jobData.specialty &&
              !excludedSpecialties.includes(jobData.specialty)
                ? `${specialtyOptions[jobData.specialty] || jobData.specialty} `
                : ''}
              {jobData.position
                ? `${
                    isDynamicView(jobData)
                      ? jobData.position
                      : positionCodeToDisplayName[jobData.position]
                  } `
                : ''}
              Job in {jobData.city}, {jobData.state}
            </h1>
            <SocialSharing
              url={`https://www.dentalpost.net/dental-jobs/zones/?id=${
                isDynamicView(jobData) ? jobData.jobId : jobData.id
              }`}
            />
            <p className="mt-3">
              <strong>Job ID:</strong>{' '}
              {isDynamicView(jobData) ? jobData.jobId : jobData.id}{' '}
              <Badge color="light">{jobData.status}</Badge>
              <br />
              <strong>Updated:</strong>{' '}
              {dayjs(jobData.updatedDate).format('MMM DD, YYYY')}
            </p>
          </div>
          {isDpAuthJob(jobData) && isStaffType(user) && (
            <>
              <div className="col-12 col-lg-auto ms-auto order-0 order-lg-2 d-flex justify-content-between gap-4">
                <div>
                  <JobPostEvaluation
                    job={jobData}
                    updateJob={(job) => {
                      if (!!setCurrentJob) {
                        setCurrentJob(job);
                      }
                      updatePremiumJob(job);
                    }}
                  />
                </div>
                {(!jobData.invitedDate ||
                  invitation?.invitation.declinedDate) &&
                  applyButton}
              </div>
              <div className="col-12 d-lg-none">
                <hr />
              </div>
            </>
          )}
        </div>
        {'isEmpProfilePublic' in jobData && jobData.isEmpProfilePublic ? (
          <>
            <Nav tabs fill className="mt-5">
              <NavItem
                className={tab === 'JOB' ? '' : 'bg-off-white rounded-top'}
              >
                <NavLink
                  href="#"
                  className="link-black fw-bold"
                  onClick={() => setTab('JOB')}
                  active={tab === 'JOB'}
                >
                  Job Posting Details
                </NavLink>
              </NavItem>
              <NavItem
                className={tab === 'EMPLOYER' ? '' : 'bg-off-white rounded-top'}
              >
                <NavLink
                  href="#"
                  className="link-black fw-bold"
                  onClick={() => setTab('EMPLOYER')}
                  active={tab === 'EMPLOYER'}
                >
                  Employer Profile
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent className="p-3 border-start border-end border-bottom">
              {tab === 'JOB' && jobPostingDetails}
              {tab === 'EMPLOYER' && (
                <PublicEmployerPage employer={jobData.employer} />
              )}
            </TabContent>
          </>
        ) : (
          <>
            <div>
              <hr />
            </div>
            {jobPostingDetails}
          </>
        )}
      </>
    );
  } else if (isPartnerAuthJob(jobData) || isPartnerCsvJob(jobData)) {
    return (
      <Container className="p-3">
        <Row className="align-items-center mb-4">
          <Col xs="12" md="auto" className="mx-auto mb-3">
            <div style={{ height: '80px', width: '80px' }}>
              <ProfilePicture isThirdParty={true} alt={jobData.company} />
            </div>
          </Col>
          <Col>
            <h1 className="h3 fw-bold">
              {htmlDecode(jobData.title)}
              <p>
                <small>(Job ID: {jobData.jobKey})</small>
              </p>
            </h1>
          </Col>
          {isPartnerAuthJob(jobData) && (
            <Col xs="auto" className="ms-auto">
              {applyButton}
            </Col>
          )}
        </Row>
        <Row className="mb-3">
          <Col md="6">
            <h3 className="h6 fw-bold">Location</h3>
            <p>
              {jobData.city}, {jobData.state} {jobData.zip}
            </p>
            <h3 className="h6 fw-bold">Details</h3>
            <h4 className="h6">
              <small>DATE POSTED</small>
            </h4>
            <p>{dayjs(jobData.postedAt).format('MMM DD, YYYY')}</p>
            <h4 className="h6">
              <small>JOB ID</small>
            </h4>
            <p>#{jobData.jobKey}</p>
            <h4 className="h6">
              <small>COMPENSATION</small>
            </h4>
            <p>{jobData.salary}</p>
          </Col>
          <Col md="6" style={{ height: '300px' }}>
            <MapWrapper jobs={[jobData]} />
          </Col>
        </Row>

        <h3 className="h6 fw-bold">Description</h3>
        <p
          className="dp-text-pre-wrap"
          dangerouslySetInnerHTML={{ __html: jobData.description }}
        ></p>
        <h3 className="h6 fw-bold">Employer Details</h3>

        <h4 className="h6">
          <small>EMPLOYER NAME</small>
        </h4>

        <div>
          <p>{jobData.company || 'Not provided'}</p>
        </div>
        <h4 className="h6">
          <small>LOCATION</small>
        </h4>
        <p>
          {jobData.city}, {jobData.state} {jobData.zip}
        </p>

        <Row>
          {isPartnerCsvJob(jobData) && <Col>{applyButton}</Col>}
          <Col className="d-flex justify-content-end align-items-end">
            <Button
              className="ms-auto text-muted"
              color="link"
              title="Report this job posting"
              onClick={() => setReportJobModalOpen(true)}
            >
              <FaFlag />
              <span className="d-none d-md-inline-block ms-2">
                Report this job posting
              </span>
            </Button>
          </Col>
        </Row>
        <ReportJobForm
          jobId={jobData.jobKey}
          flagType={'NONE'}
          isThirdParty={true}
          reportJobModalOpen={reportJobModalOpen}
          setReportJobModalOpen={setReportJobModalOpen}
        />
      </Container>
    );
  } else if (isCsvJob(jobData)) {
    return (
      <Container className="p-3">
        <Row className="align-items-center mb-4">
          <Col xs="12" md="auto" className="mx-auto mb-3">
            <div style={{ height: '120px', width: '120px' }}>
              <ProfilePicture
                isThirdParty={jobData.source !== 'DentalPost'}
                alt={jobData.source}
              />
            </div>
          </Col>
          <Col>
            <h1 className="h3 fw-bold">
              {jobData.source !== 'DentalPost' ? (
                htmlDecode(jobData.title)
              ) : (
                <p>
                  {jobData.specialty &&
                  !excludedSpecialties.includes(jobData.specialty)
                    ? `${
                        specialtyOptions[jobData.specialty] || jobData.specialty
                      } `
                    : ''}
                  {jobData.position
                    ? `${positionCodeToDisplayName[jobData.position]} `
                    : ''}
                  Job in {jobData.city}, {jobData.state}
                </p>
              )}
              {jobData.isNational === 'true' && (
                <Badge color="secondary" className="ms-3">
                  National
                </Badge>
              )}
              <p>
                <small>(Job ID: {jobData.jobId})</small>
              </p>
            </h1>
            <SocialSharing
              url={`https://www.dentalpost.net/dental-jobs${jobData.uri}/`}
            />
          </Col>
        </Row>
        <Row className="mb-3">
          <Col md="6">
            <h3 className="h6 fw-bold">Location</h3>
            <p>
              {!!listing && (
                <Link to={listing}>
                  {jobData.city}, {jobData.state} {jobData.zip}
                </Link>
              )}
            </p>
            <h3 className="h6 fw-bold">Details</h3>
            <h4 className="h6">
              <small>DATE POSTED/UPDATED</small>
            </h4>
            <p>
              {dayjs(
                !!jobData.updatedDate
                  ? jobData.updatedDate
                  : jobData.createdDate
              ).format('MMM DD, YYYY')}
            </p>
            <h4 className="h6">
              <small>JOB TYPE</small>
            </h4>
            <p>
              {jobData.isNational === 'true'
                ? 'National'
                : jobData.source === 'DentalPost'
                  ? !!jobData.jobDates
                    ? 'Temp'
                    : 'Permanent'
                  : 'Not Provided'}
            </p>
            <h4 className="h6 mb-0">
              <small>COMPENSATION</small>
            </h4>
            <p className="mb-0 text-info">
              <small>Base Salary</small>
            </p>
            <p>
              {!!jobData.salary
                ? formatSalary(jobData.salary, jobData.salaryUnits)
                : 'Not Provided'}
            </p>
            <h4 className="h6">
              <small>
                {!!jobData.jobDates && jobData.jobDates.split(',').length > 0
                  ? 'DATES'
                  : 'DAYS OF WEEK'}
              </small>
            </h4>
            {jobData.source === 'DentalPost' &&
            !!jobData.jobDates &&
            jobData.jobDates.split(',').length > 0 ? (
              <ul style={{ listStyle: 'none' }} className="ps-0">
                {jobData.jobDates.split(',').map((day) => {
                  return (
                    <li key={day}>{dayjs(day).format('ddd MMMM D, YYYY')}</li>
                  );
                })}
              </ul>
            ) : (
              <p>
                {jobData.source === 'DentalPost' ? jobData.dow : 'Not Provided'}
              </p>
            )}
            <h4 className="h6">
              <small>JOB ID / STATUS</small>
            </h4>
            <p>
              #{jobData.jobId}{' '}
              {!dayjs(jobData.expireDate).isAfter(dayjs()) ? (
                <Badge color="light">Expired </Badge>
              ) : (
                <Badge color="success">Active</Badge>
              )}
            </p>

            {jobData.isConfidential !== 'true' && !jobData.expireDate && (
              <>
                <h4 className="h6">
                  <small>EMPLOYER</small>
                </h4>

                <div>
                  <p>{jobData.name || 'Not provided'}</p>
                </div>
              </>
            )}
          </Col>
          <Col md="6" style={{ height: '300px' }}>
            <MapWrapper jobs={[jobData]} />
          </Col>
        </Row>

        <h3 className="h6 fw-bold">Description</h3>
        <p
          className="dp-text-pre-wrap"
          dangerouslySetInnerHTML={{ __html: jobData.description }}
        ></p>

        {jobData.isConfidential === 'true' && (
          <Alert color="info">
            <p className="mb-0">
              <Badge color="dark">
                <FaLock /> Confidential
              </Badge>
            </p>
            <p className="mb-0">
              <small>
                Note: This is a confidential job posting. Some of the employer's
                information is not displayed.
              </small>
            </p>
          </Alert>
        )}
        <Row>
          <Col xs="auto">
            <p>Create your free account with DentalPost to apply.</p>
            <Button
              color="primary"
              className="mb-0"
              onClick={() => {
                updateRedirectPath(`${toJobSearchJobDetail(jobData.jobId)}`);
                navigate(toStaffRegistration);
              }}
            >
              Register & Apply
            </Button>
          </Col>
          <Col className="d-flex justify-content-end align-items-end">
            <Button
              className="ms-auto text-muted"
              color="link"
              title="Report this job posting"
              onClick={() => setReportJobModalOpen(true)}
            >
              <FaFlag />
              <span className="d-none d-md-inline-block ms-2">
                Report this job posting
              </span>
            </Button>
          </Col>
        </Row>
        <ReportJobForm
          jobId={jobData.jobId}
          flagType={'NONE'}
          isThirdParty={jobData.source !== 'DentalPost'}
          reportJobModalOpen={reportJobModalOpen}
          setReportJobModalOpen={setReportJobModalOpen}
        />
      </Container>
    );
  } else {
    return <div>unknown type={JSON.stringify(jobData)}</div>;
  }
};

export default JobPostingView;
